import type {
  ComplianceCardListData,
  ContentBlock,
  ExpiringCertificateCardData,
  InvoiceCardData,
  KPIGaugeData,
  OpenViolationCardData,
  PerformanceTableData,
  PricingTableData,
  PropertyCardData,
  ReviewRuleCategory,
  RuleSuggestionsCardData,
  StatHighlightData,
  SuggestedReviewRule,
  TicketCardData,
  TrendChartData,
  UnitCardData,
  VendorCardData,
} from '~/components/intelligence/ChatComponents';
import type {
  KnowledgeChatTarget,
  KnowledgeResultItem,
  KnowledgeTargetOption,
} from '~/components/knowledge/KnowledgeChatCards';
import type { KnowledgeEntryKindEnum, KnowledgeEntrySourceEnum } from '~/data/generated/model';

const REVIEW_RULE_CATEGORIES: ReviewRuleCategory[] = [
  'review-rule',
  'included-service',
  'excluded-service',
  'miscellaneous-charge',
  'other-term',
];

function toReviewRuleCategory(value: unknown): ReviewRuleCategory {
  return REVIEW_RULE_CATEGORIES.includes(value as ReviewRuleCategory)
    ? (value as ReviewRuleCategory)
    : 'review-rule';
}

function flattenAddress(addr: unknown): string {
  if (typeof addr === 'string') return addr;
  if (addr && typeof addr === 'object') {
    const a = addr as { formatted?: string; raw?: string };
    return a.formatted ?? a.raw ?? '';
  }
  return '';
}

function flattenPortfolio(p: unknown): string | undefined {
  if (typeof p === 'string') return p || undefined;
  if (p && typeof p === 'object') {
    const obj = p as { name?: string };
    return obj.name || undefined;
  }
  return undefined;
}

function flattenName(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const obj = value as { name?: string };
    return obj.name ?? '';
  }
  return '';
}

type ToolEnvelope = {
  data?: unknown[];
  truncated?: boolean;
  total_count?: number;
  filters_applied?: Record<string, unknown>;
};

function isEnvelope(output: unknown): output is ToolEnvelope {
  return !!output && typeof output === 'object' && 'data' in output;
}

function toPropertyCards(output: unknown): ContentBlock | null {
  if (!isEnvelope(output) || !Array.isArray(output.data)) return null;
  const properties: PropertyCardData[] = output.data.map((row) => {
    const r = row as Record<string, unknown>;
    return {
      id: String(r.id ?? ''),
      name: String(r.name ?? ''),
      address: flattenAddress(r.address),
      unitCount: typeof r.unitCount === 'number' ? r.unitCount : 0,
      portfolio: flattenPortfolio(r.portfolio),
      callbackRate: typeof r.callbackRate === 'number' ? r.callbackRate : undefined,
      maintenanceCompletion:
        typeof r.maintenanceCompletion === 'number' ? r.maintenanceCompletion : undefined,
    };
  });
  return { type: 'property-cards', properties };
}

function toInvoiceCards(output: unknown): ContentBlock | null {
  if (!isEnvelope(output) || !Array.isArray(output.data)) return null;
  const invoices: InvoiceCardData[] = output.data.map((row) => {
    const r = row as Record<string, unknown>;
    const amount =
      typeof r.proposedAmount === 'number'
        ? r.proposedAmount
        : typeof r.proposedAmount === 'string'
          ? Number(r.proposedAmount)
          : 0;
    return {
      id: String(r.id ?? ''),
      invoiceNumber: String(r.number ?? ''),
      vendor: flattenName(r.serviceProvider),
      property: flattenName(r.property),
      amount: Number.isFinite(amount) ? amount : 0,
      status: (r.status as InvoiceCardData['status']) ?? 'pending',
      date: String(r.date ?? ''),
      kind: r.kind === 'proposal' || r.kind === 'credit' ? r.kind : 'invoice',
    };
  });
  return { type: 'invoice-cards', invoices };
}

function toUnitCards(output: unknown): ContentBlock | null {
  if (!isEnvelope(output) || !Array.isArray(output.data)) return null;
  const units: UnitCardData[] = output.data.map((row) => {
    const r = row as Record<string, unknown>;
    return {
      id: String(r.id ?? ''),
      unitName: String(r.name ?? ''),
      propertyName: flattenName(r.property),
      manufacturer: typeof r.manufacturer === 'string' ? r.manufacturer : undefined,
      type: typeof r.elevatorType === 'string' ? r.elevatorType : undefined,
      callbackRate: typeof r.callbackRate === 'number' ? r.callbackRate : undefined,
      callbackRateTarget:
        typeof r.callbackRateTarget === 'number' ? r.callbackRateTarget : undefined,
      maintenanceCompletion:
        typeof r.maintenanceCompletion === 'number' ? r.maintenanceCompletion : undefined,
    };
  });
  return { type: 'unit-cards', units };
}

function toVendorCards(output: unknown): ContentBlock | null {
  let rows: unknown[] = [];
  if (Array.isArray(output)) {
    rows = output;
  } else if (isEnvelope(output) && Array.isArray(output.data)) {
    rows = output.data;
  } else if (output && typeof output === 'object') {
    const o = output as { data?: unknown };
    if (Array.isArray(o.data)) rows = o.data;
  }
  const num = (value: unknown) => (typeof value === 'number' ? value : undefined);
  const vendors: VendorCardData[] = rows.map((row) => {
    const r = row as Record<string, unknown>;
    return {
      id: String(r.id ?? ''),
      name: String(r.name ?? ''),
      callbacks: num(r.callbacks),
      emergencyCallbacks: num(r.emergencyCallbacks),
      maintenanceVisits: num(r.maintenanceVisits),
      contractCount: num(r.contractCount),
      // Sent only by render_vendor_cards before it stopped averaging contract rates;
      // still mapped so a session saved to localStorage before then renders as it did.
      callbackRate: typeof r.callbackRate === 'number' ? r.callbackRate : undefined,
      maintenanceCompletion:
        typeof r.maintenanceCompletion === 'number' ? r.maintenanceCompletion : undefined,
      onTimeResponse: typeof r.onTimeResponse === 'number' ? r.onTimeResponse : undefined,
    };
  });
  return { type: 'vendor-cards', vendors };
}

function toPricingTable(output: unknown): ContentBlock | null {
  const o = (output && typeof output === 'object' ? output : {}) as { data?: unknown };
  const inner = o.data && typeof o.data === 'object' ? (o.data as Record<string, unknown>) : null;
  if (!inner) return null;
  const title = typeof inner.title === 'string' ? inner.title : 'Spending';
  const rawRows = Array.isArray(inner.rows) ? inner.rows : [];
  const rows = rawRows.map((r) => {
    const row = r as Record<string, unknown>;
    const values = Array.isArray(row.values) ? row.values : [];
    return {
      label: String(row.label ?? ''),
      values: values.map((v) => {
        const val = v as Record<string, unknown>;
        return {
          vendor: String(val.vendor ?? ''),
          amount: typeof val.amount === 'number' ? val.amount : 0,
        };
      }),
    };
  });
  const data: PricingTableData = { title, rows };
  return { type: 'pricing-table', data };
}

function toTicketCards(output: unknown): ContentBlock | null {
  if (!isEnvelope(output) || !Array.isArray(output.data)) return null;
  const tickets: TicketCardData[] = output.data.map((row) => {
    const r = row as Record<string, unknown>;
    const status = String(r.status ?? '').toLowerCase();
    const normalizedStatus: TicketCardData['status'] =
      status === 'closed' ? 'closed' : status === 'in_progress' ? 'in_progress' : 'open';
    return {
      id: String(r.id ?? ''),
      ticketNumber: String(r.number ?? ''),
      title: String(r.problemDescription ?? r.notes ?? ''),
      property: flattenName(r.property),
      status: normalizedStatus,
      date: String(r.startedTime ?? r.createdAt ?? ''),
      category: typeof r.entryType === 'string' ? r.entryType : undefined,
    };
  });
  return { type: 'ticket-cards', tickets };
}

function toMetricSet(output: unknown): ContentBlock | null {
  if (!output || typeof output !== 'object') return null;
  const o = output as { display?: unknown; data?: unknown };
  const data = o.data;
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;

  if (o.display === 'kpi-gauge') {
    const gauges = Array.isArray(d.gauges) ? (d.gauges as KPIGaugeData[]) : [];
    return { type: 'kpi-gauge', gauges };
  }
  if (o.display === 'stat-highlights') {
    const stats = Array.isArray(d.stats) ? (d.stats as StatHighlightData[]) : [];
    return { type: 'stat-highlights', stats };
  }
  if (o.display === 'performance-table') {
    const table = d.table as PerformanceTableData | undefined;
    if (!table || typeof table !== 'object') return null;
    return { type: 'performance-table', data: table };
  }
  return null;
}

function toTrendChart(output: unknown): ContentBlock | null {
  if (!output || typeof output !== 'object') return null;
  const o = output as { data?: unknown };
  const data = o.data as TrendChartData | undefined;
  if (!data || typeof data !== 'object') return null;
  return { type: 'trend-chart', data };
}

function toComplianceCards(output: unknown): ContentBlock | null {
  if (!output || typeof output !== 'object') return null;
  const o = output as { data?: unknown };
  const raw = (o.data ?? {}) as Record<string, unknown>;
  const data: ComplianceCardListData = {};
  if (Array.isArray(raw.expiring_certificates)) {
    data.expiringCertificates = (raw.expiring_certificates as Record<string, unknown>[]).map(
      (row): ExpiringCertificateCardData => ({
        id: String(row.id ?? ''),
        carId: (row.carId as string | undefined) ?? null,
        kind: (row.kind as string | undefined) ?? null,
        expiresOn: (row.expiresOn as string | undefined) ?? null,
        jurisdiction: (row.jurisdiction as string | undefined) ?? null,
        propertyName: (row.propertyName as string | undefined) ?? null,
        unitName: (row.unitName as string | undefined) ?? null,
      }),
    );
  }
  if (Array.isArray(raw.open_violations)) {
    data.openViolations = (raw.open_violations as Record<string, unknown>[]).map(
      (row): OpenViolationCardData => ({
        id: String(row.id ?? ''),
        carId: (row.carId as string | undefined) ?? null,
        code: (row.code as string | undefined) ?? null,
        correctionDue: (row.correctionDue as string | undefined) ?? null,
        responsibility: (row.responsibility as string | undefined) ?? null,
        status: row.status === 'overdue' ? 'overdue' : 'open',
        propertyName: (row.propertyName as string | undefined) ?? null,
        unitName: (row.unitName as string | undefined) ?? null,
      }),
    );
  }
  return { type: 'compliance-cards', data };
}

// Tool output is the snake_case dict the Python tool emits — it is NOT passed
// through the API's camelCase renderer (mirrors toComplianceCards). Map the
// keys to the camelCase card shape here.
function toRuleSuggestions(output: unknown): ContentBlock | null {
  if (!output || typeof output !== 'object') return null;
  const o = output as { data?: unknown };
  const raw = (o.data ?? {}) as Record<string, unknown>;
  const contractId = typeof raw.contract_id === 'string' ? raw.contract_id : '';
  if (!contractId) return null;

  const rulesRaw = Array.isArray(raw.suggested_rules) ? raw.suggested_rules : [];
  const suggestedRules: SuggestedReviewRule[] = rulesRaw
    .map((row): SuggestedReviewRule | null => {
      const r = row as Record<string, unknown>;
      const reviewRule = typeof r.review_rule === 'string' ? r.review_rule : '';
      if (!reviewRule) return null;
      return {
        reviewRule,
        category: toReviewRuleCategory(r.category),
      };
    })
    .filter((r): r is SuggestedReviewRule => r !== null);

  if (suggestedRules.length === 0) return null;

  const conflictingRuleIds = Array.isArray(raw.conflicting_rule_ids)
    ? raw.conflicting_rule_ids.filter((id): id is string => typeof id === 'string')
    : [];

  const data: RuleSuggestionsCardData = { contractId, suggestedRules, conflictingRuleIds };
  return { type: 'rule-suggestions', data };
}

const KNOWLEDGE_KINDS: KnowledgeEntryKindEnum[] = ['note', 'decision', 'commitment'];

const isKnowledgeKind = (value: unknown): value is KnowledgeEntryKindEnum =>
  KNOWLEDGE_KINDS.includes(value as KnowledgeEntryKindEnum);

const toKnowledgeKind = (value: unknown): KnowledgeEntryKindEnum =>
  isKnowledgeKind(value) ? value : 'note';

const toKnowledgeSource = (value: unknown): KnowledgeEntrySourceEnum =>
  value === 'assistant' || value === 'task' ? value : 'manual';

const optionalString = (value: unknown) => (typeof value === 'string' && value ? value : undefined);

function toKnowledgeTarget(value: unknown): KnowledgeChatTarget | null {
  if (!value || typeof value !== 'object') return null;
  const t = value as Record<string, unknown>;
  if (typeof t.property_id !== 'string' || !t.property_id) return null;
  return {
    propertyId: t.property_id,
    propertyName: typeof t.property_name === 'string' ? t.property_name : '',
    carId: optionalString(t.car_id),
    carName: optionalString(t.car_name),
  };
}

function knowledgeData(output: unknown): Record<string, unknown> | null {
  if (!output || typeof output !== 'object') return null;
  const data = (output as { data?: unknown }).data;
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
}

function toKnowledgeDraft(output: unknown): ContentBlock | null {
  const raw = knowledgeData(output);
  const target = toKnowledgeTarget(raw?.target);
  if (!raw || !target || typeof raw.content !== 'string') return null;
  return {
    type: 'knowledge-draft',
    data: {
      target,
      kind: toKnowledgeKind(raw.kind),
      content: raw.content,
      suggested: raw.suggested === true,
    },
  };
}

function toKnowledgeResults(output: unknown): ContentBlock | null {
  const raw = knowledgeData(output);
  if (!raw) return null;
  const rows = Array.isArray(raw.results) ? raw.results : [];
  const results = rows
    .map((row): KnowledgeResultItem | null => {
      const r = row as Record<string, unknown>;
      const target = toKnowledgeTarget(r.target);
      if (
        !target ||
        typeof r.id !== 'string' ||
        typeof r.content !== 'string' ||
        typeof r.created_at !== 'string' ||
        Number.isNaN(Date.parse(r.created_at))
      ) {
        return null;
      }
      return {
        id: r.id,
        kind: toKnowledgeKind(r.kind),
        content: r.content,
        authorName: typeof r.author_name === 'string' ? r.author_name : '',
        createdAt: r.created_at,
        source: toKnowledgeSource(r.source),
        target,
      };
    })
    .filter((r): r is KnowledgeResultItem => r !== null);
  return {
    type: 'knowledge-results',
    data: {
      query: typeof raw.query === 'string' ? raw.query : '',
      target: toKnowledgeTarget(raw.target),
      results,
    },
  };
}

function toKnowledgeTargetChoice(output: unknown): ContentBlock | null {
  const raw = knowledgeData(output);
  if (!raw) return null;
  const options = (Array.isArray(raw.options) ? raw.options : [])
    .map((value): KnowledgeTargetOption | null => {
      const target = toKnowledgeTarget(value);
      if (!target) return null;
      const label = (value as Record<string, unknown>).label;
      return { ...target, label: typeof label === 'string' && label ? label : target.propertyName };
    })
    .filter((t): t is KnowledgeTargetOption => t !== null);
  if (options.length === 0) return null;
  return {
    type: 'knowledge-target-choice',
    data: {
      question: typeof raw.question === 'string' ? raw.question : 'Which one?',
      options,
    },
  };
}

function toKnowledgeContentPrompt(output: unknown): ContentBlock | null {
  const raw = knowledgeData(output);
  if (!raw) return null;
  return {
    type: 'knowledge-content-prompt',
    data: {
      target: toKnowledgeTarget(raw.target),
      kind: isKnowledgeKind(raw.kind) ? raw.kind : null,
    },
  };
}

export const TOOL_ADAPTERS: Record<string, (output: unknown) => ContentBlock | null> = {
  render_property_cards: toPropertyCards,
  render_invoice_cards: toInvoiceCards,
  render_unit_cards: toUnitCards,
  render_ticket_cards: toTicketCards,
  render_vendor_cards: toVendorCards,
  render_pricing_table: toPricingTable,
  render_metric_set: toMetricSet,
  render_trend_chart: toTrendChart,
  render_compliance_cards: toComplianceCards,
  render_rule_suggestions: toRuleSuggestions,
  render_knowledge_draft: toKnowledgeDraft,
  search_knowledge: toKnowledgeResults,
  ask_knowledge_target: toKnowledgeTargetChoice,
  ask_knowledge_content: toKnowledgeContentPrompt,
};

export const SILENT_TOOLS = new Set([
  'aggregate',
  'find_properties',
  'find_portfolios',
  'find_vendors',
  'get_ticket_details',
  'get_invoice_details',
  'get_unit_details',
  'render_property_summary',
  'get_rule_suggestion_context',
]);
